import { useEffect, useState, useMemo } from "react";
import ReactFlow, { useReactFlow } from 'reactflow';
import "reactflow/dist/style.css";
import UnsFlowNode from "./UnsFlowNode";
import UnsEdge from "./UnsEdge";
import UnsBackgroundBox from "./UnsBackgroundBox";
import { useFlowDiagram } from "../../hooks/useFlowDiagram";

const nodeTypes = {
  UnsFlowNode: UnsFlowNode,
  UnsBackgroundBox: UnsBackgroundBox
};

const edgeTypes = {
    unsedge: UnsEdge,
  };

const UnsFlow = ({hotTopic, flowId}) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const { fitView } = useReactFlow();

  // Read flow diagram data from Firestore via real-time subscription
  const { diagram, flowNodes, backgroundBoxes, loading } = useFlowDiagram(flowId);

  useEffect(() => {
    // Automatically zoom to fit the view
    fitView({ padding: 0.1, maxZoom: 2 });
  }, [fitView]);

  // Memoize the flow data to avoid unnecessary recalculations
  const flow = useMemo(() => {
    if (!diagram) return null;
    return {
      flow: flowNodes,
      backgroundBoxes: backgroundBoxes,
    };
  }, [diagram, flowNodes, backgroundBoxes]);

  useEffect(() => {
    if (!flow || flow.flow.length === 0) return;

    // Build background box nodes from Firestore data
    const bgNodes = (flow.backgroundBoxes || []).map((box) => ({
      id: box.id,
      type: 'UnsBackgroundBox',
      position: box.position,
      data: {
        label: box.label,
        imageUrl: box.imageUrl,
        width: box.width,
        height: box.height,
      },
      style: { zIndex: -1 },
      draggable: false,
      selectable: false,
    }));

    const initialNodes = [
      ...bgNodes,
      ...flow.flow.map((item) => ({
        id: item.id,
        position: item.position,
        data: {
          ...item,
          hotTopic: hotTopic,
          onClick: handleNodeClick,
        },
        type: item.nodeType || "default",
      }))
    ];

    const initialEdges = flow.flow
      .filter((item) => item.source && item.target)
      .map((item) => {
        const sourceItem = flow.flow.find((searchItem) => searchItem.source === item.source);
        const targetItem = flow.flow.find((searchItem) => searchItem.id === item.target);
        const isHighlighted =
          (typeof hotTopic === 'string' && hotTopic.includes(item.topic)) ||
          (Array.isArray(item.topic) && item.topic.some(t => typeof hotTopic === 'string' && hotTopic.includes(t)));

        const edge = {
          id: `e${item.source}-${item.target}`,
          source: item.source,
          target: item.target,
          animated: !isHighlighted,
          label: item.protocol,
          data: {
            topic: item.topic,
            hotTopic: hotTopic,
            targetPosition: item.target ? flow.flow.find((searchItem) => searchItem.id === item.target).targetPosition : 'left',
            sourcePosition: item.sourceOverride ? item.sourceOverride : flow.flow.find((searchItem) => searchItem.source === item.source).sourcePosition,
          },
          sourceX: sourceItem?.position?.x,
          sourceY: sourceItem?.position?.y,
          targetX: targetItem?.position?.x,
          targetY: targetItem?.position?.y,
          type: "unsedge",
          style: { stroke: "#fff" }
        };

        return edge;
      });

    setNodes(initialNodes);
    setEdges(initialEdges);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, hotTopic]);

  const handleNodeClick = (id) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          node.data = { ...node.data, active: !node.data.active };
        }
        return node;
      })
    );
  };


  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 10,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          color: "white",
          fontSize: "26px",
          fontWeight: "bold",
        }}
      >
      </div>
      {loading ? (
        <div style={{ color: 'white', textAlign: 'center', paddingTop: '40px' }}>
          Loading architecture diagram...
        </div>
      ) : (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          style={{ background: 'transparent' }}
        />
      )}
    </div>
  );
};

export default UnsFlow;
